//update Halimunnisa 09-06-2026
//Created a new Customer Detail Modal for Verve Contacts, reusing the architecture from the Leads Detail Modal but with a simplified field set and a focus on contact-specific information and activities. The status update selector is hidden as per original code, and the modal emphasizes notes and appointment details relevant to contact interactions.
import React, { useState } from "react";
import { DetailsWorkspaceModal } from "@/components/ui";
import { SectionTitle, Caption } from "@/components/ui/Typography";
import { fmtDate } from "@/lib/formatters";
import { User, Tag, Mail, Phone, Home, MapPin, Building, Link, Briefcase, Calendar, IndianRupee } from "lucide-react";
import { SiteVisitAccordion } from "../../client_contact_stages/Site-Visit/components/SiteVisitAccordion";
import { InitialQuotationAccordion } from "../../client_contact_stages/Initial-Quotation/components/InitialQuotationAccordion";
/* Developer : Nithin - Added InitialDesignAccordion */
import { InitialDesignAccordion } from "../../verve-leads/leads/components/InitialDesignAccordion";
import { OrderingAccordion } from "../../ordering/components/OrderingAccordion";
import { UploadDocumentsAccordion } from "../../verve-leads/leads/components/UploadDocumentsAccordion";
import { InterestedDocumentsAccordion } from "../../verve-leads/leads/components/InterestedDocumentsAccordion";
import { MediaPreviewModal } from "../../client_contact_stages/verve-design/components/MediaPreviewModal";

const pickFirst = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== "");

const numOrUndef = (v) => {
  if (v === undefined || v === null || v === "") return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
};
// Helper function to format status labels
//updated by: Halimunnisa 09-06-2026
export const CustomerDetailModal = ({
  isOpen,
  detail,
  onClose,
  activities = [],
  actNote,
  setActNote,
  onAddActivity,
  updateStatus,
  setDetail,
  stages,
  users,
  user
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);

  const handlePreview = (file) => {
    setSelectedMedia(file);
    setIsMediaModalOpen(true);
  };

  if (!detail) return null;

  const displayName =
    detail.customerName ||
    [detail.firstName, detail.lastName].filter(Boolean).join(" ") ||
    detail.customId ||
    detail.customID ||
    "Customer";

  const customId = detail.customId || detail.customID || detail.id || detail._id || "";
  const phone = detail.phoneNumber || detail.phone || "";
  const email = detail.email || "";

  const roleString = user?.role?.toLowerCase()?.trim() || "";
  const isRestrictedRole = ["owner", "telecaller", "telecalling", "customer"].includes(roleString);
  const isProjectStarted = ["project_started", "project_start"].includes(detail.status);
  const displayStatus = (isRestrictedRole && isProjectStarted) ? "converted" : (detail.teleStatus || detail.status);
  const stage = stages?.find(s => s.key === displayStatus);
  const labelText = stage ? stage.label : (displayStatus?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'Unknown');

  const isFullyLocked = detail.isDisabled || ["project_completed", "completed", "project_started", "project_start"].includes(detail.status);
  //updated by: Halimunnisa 09-06-2026
  // Extract other details
  const d = detail || {};
  const l = detail.leadId || {};

  const serviceType = pickFirst(d.leadId?.serviceType, l.serviceType, "Verve");
  const isVerve = String(serviceType).toLowerCase() === "Verve";

  const cityVal = pickFirst(d.city, l.city);
  const stateVal = pickFirst(d.state, l.state);
  const addressLine1 = pickFirst(d.addressLine1, l.addressLine1, d.address, l.address, d.address1, l.address1);
  const addressLine2 = pickFirst(d.addressLine2, l.addressLine2);
  const pincode = pickFirst(d.pincode, d.leadId?.pincode, l.pincode);

  const requirement = pickFirst(d.propertyType, l.propertyType, d.VerveRequirement, l.VerveRequirement);
  const billRange = pickFirst(d.electricityBill, d.electricityBillRange, l.electricityBill, l.electricityBillRange);

  const execCapacityRaw = numOrUndef(pickFirst(d.capacity, d.VerveCapacity, d.VerveCapacityRequested));
  const capacityKw = execCapacityRaw && execCapacityRaw > 0 ? execCapacityRaw : undefined;

  const execBudgetRaw = numOrUndef(pickFirst(d.budget, d.totalCost, d.leadId?.budget));
  const budget = execBudgetRaw && execBudgetRaw > 0 ? execBudgetRaw : undefined;

  const telecaller = pickFirst(
    d.telecaller?.name,
    d.telecallerId?.name,
    d.telecallerInfo?.name,
    d.leadId?.telecallerId?.name,
    l.telecallerId?.name,
    l.telecaller?.name
  );

  const referenceSource = pickFirst(d.referenceType, l.referenceType, d.leadId?.referenceType, d.source, l.source);
  const referenceDetails = pickFirst(d.referenceDetails, l.referenceDetails, d.referenceLabel, l.referenceLabel, d.leadId?.referenceLabel, d.referralDetails, l.referralDetails, d.sourceName, l.sourceName);

  const meetingDate = pickFirst(d.preferredDate, l.preferredDate);
  const altMeetingDate = pickFirst(d.alternativeMeetingDate, l.alternativeMeetingDate);
  const attendedBy = pickFirst(d.attendedBy, l.attendedBy);

  const formatAppt = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const formattedDate = fmtDate(dateStr);
    
    if (typeof dateStr === 'string' && (dateStr.length <= 10 || dateStr.endsWith("T00:00:00.000Z"))) {
      return `${formattedDate} at -`;
    }
    
    const timeStr = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return `${formattedDate} at ${timeStr}`;
  };

  // Find notes
  const initialActivity = (activities || []).find(a => a.action === 'Contact Created' || a.action === 'Lead Created');
  let initialNote = initialActivity?.note || initialActivity?.comment || detail?.notes || "";
  if (initialNote === 'Contact manually added to the system') initialNote = "";

  const manualNoteActivity = (activities || []).find(a => 
      a.action === 'Note Added' || 
      (a.note && a.note.includes('| Note:')) || 
      (a.action === 'Contact Created' && a.note && a.note !== 'Contact manually added to the system')
  );

  const lastNoteRaw = manualNoteActivity?.note || manualNoteActivity?.comment || detail?.notes || "";
  const lastNote = lastNoteRaw.includes('| Note:') ? lastNoteRaw.split('| Note:')[1].trim() : (lastNoteRaw === 'Contact manually added to the system' ? "" : lastNoteRaw);

  // Fields array matching the Leads workspace style
  const fields = [
    { icon: User, label: "Full Name", value: displayName },
    customId && { icon: Tag, label: "Contact ID", value: (customId || "").toUpperCase() },
    email && { icon: Mail, label: "Email Address", value: email },
    phone && { icon: Phone, label: "Phone Number", value: phone },
    { icon: Briefcase, label: "Contact Status", value: labelText },
    detail.createdAt && { icon: Calendar, label: "Created At", value: fmtDate(detail.createdAt) },
    addressLine1 && { icon: Home, label: "Address Line 1", value: addressLine1 },
    addressLine2 && { icon: Home, label: "Address Line 2", value: addressLine2 },
    cityVal && { icon: MapPin, label: "City", value: cityVal },
    pickFirst(d.district, l.district) && { icon: MapPin, label: "District", value: pickFirst(d.district, l.district) },
    stateVal && { icon: MapPin, label: "State", value: stateVal },
    pincode && { icon: MapPin, label: "PIN Code", value: pincode },
    pickFirst(d.officeLocation, l.officeLocation) && { icon: Building, label: "Office Location", value: pickFirst(d.officeLocation, l.officeLocation) },
    telecaller && { icon: User, label: "Telecaller", value: telecaller + (d.updatedAt ? ` (Updated: ${new Date(d.updatedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })})` : "") },
    referenceSource && { icon: Link, label: "Reference Source", value: referenceSource },
    referenceDetails && { icon: Link, label: "Referral Details", value: referenceDetails },
    requirement && { icon: Building, label: "Requirement", value: requirement },
    budget && { icon: IndianRupee, label: "Budget", value: `₹${new Intl.NumberFormat('en-IN').format(budget)}` },
    { icon: Calendar, label: "Preferred Date & Time", value: meetingDate ? formatAppt(meetingDate) : "—" },
    { icon: Calendar, label: "Alt Date & Time", value: altMeetingDate ? formatAppt(altMeetingDate) : "—" },
    { icon: User, label: "Attended By", value: attendedBy || "—" },
  ].filter(Boolean);

  const contactTimeline = (activities || [])
    .filter((a) => {
      const isMatch = (a.contactId || a.leadId) === detail._id;
      if (!isMatch) return false;
      // Only hide 'Contact Updated' if it has no manual note part
      if (a.action === 'Contact Updated') {
        return a.note && a.note.includes('| Note:');
      }
      return true;
    })
    .sort((a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date))
    .map((a, i) => {
      const actionText = a.action ? `${a.action}` : "";
      const content = a.note || a.comment || a.text || "";
      const time = (a.createdAt || a.date) ? new Date(a.createdAt || a.date).toLocaleString('en-IN') : "";

      let renderedContent;
      if (content.includes('| Note:')) {
        const parts = content.split('| Note:');
        const systemPart = parts[0].trim();
        const userPart = parts[1].trim();
        renderedContent = (
          <>
            {systemPart && <span className="text-[10px] text-slate-400 font-medium leading-tight mb-1">{systemPart}</span>}
            <span className="font-semibold text-slate-700"><span className="text-[10px] text-slate-400 mr-1.5">Note:</span>{userPart}</span>
          </>
        );
      } else {
        renderedContent = <span className="font-semibold text-slate-700">{content}</span>;
      }

      return (
        <span key={i} className="flex flex-col gap-0.5">
          {actionText && <span className="font-bold text-[11px] uppercase tracking-tight text-slate-800">{actionText}</span>}
          {renderedContent}
          <span className="text-[9px] text-slate-400 mt-0.5">{time} by {a.performedBy?.name || 'System'}</span>
        </span>
      );
    });

  const handleAddNote = async (text) => {
    setIsSubmitting(true);
    try {
      await onAddActivity(text);
    } catch (e) {
      console.error(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <DetailsWorkspaceModal
      isOpen={isOpen || !!detail}
      onClose={onClose}
      title="Verve Kitchens"
      subtitle="CALL INTERACTION DETAILS"
      fields={fields}
      timeline={contactTimeline}
      onAddNote={handleAddNote}
      isInactive={isFullyLocked}
      saving={isSubmitting}
      savingNote={isSubmitting}
    >
      <div className="flex flex-col gap-6">
        {/* Appointment Section */}
        {detail.appointmentDate && (
          <div className="bg-orange-50/40 rounded-xl p-4 border border-orange-100/60">
            <div className="text-[10px] text-orange-600 font-bold uppercase tracking-wider mb-2 flex items-center gap-2">
              <Calendar size={14} className="text-orange-500" />
              Scheduled Appointment
            </div>
            <div className="flex items-center gap-3">
              <div className="flex flex-col">
                <SectionTitle className="text-[14px] text-orange-800">
                  {new Date(detail.appointmentDate).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </SectionTitle>
                <Caption className="text-orange-600 font-medium">Site visit or meeting is scheduled for this customer.</Caption>
              </div>
            </div>
          </div>
        )}

        {/* Initial Notes Section */}
        {initialNote && (
          <div className="bg-slate-50/50 rounded-xl p-4 border border-slate-100">
            <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1.5 flex items-center gap-2">
              <span className="w-1 h-3 bg-slate-400 rounded-full" />
              Initial Notes
            </div>
            <div className="text-[12px] font-medium text-slate-700 leading-relaxed italic">
              {initialNote}
            </div>
          </div>
        )}

        {/* Conditionally hide project flow for Direct Sales */}
        {detail?.salesType !== 'Direct Sale' && (
          <>
            {/* Developer: Nithin - Added Upload If Any and Interested Documents Accordions */}
            <UploadDocumentsAccordion lead={detail} onPreview={handlePreview} />
            <InterestedDocumentsAccordion lead={detail} onPreview={handlePreview} />

            {/* Site Visit Accordion */}
            <SiteVisitAccordion lead={detail} />

            {/* Developer : Nithin - Added Initial Design Accordion to Contacts View */}
            <InitialDesignAccordion lead={detail} onPreview={handlePreview} />

            {/* Initial Quotation Accordion */}
            <InitialQuotationAccordion lead={detail} />
          </>
        )}

        {/* Ordering Quotations Accordion */}
        <OrderingAccordion lead={detail} />

        {/* Last Notes Section */}
        {lastNote && (
          <div className="bg-amber-50/50 rounded-xl p-4 border border-amber-100">
            <div className="text-[10px] text-amber-600 font-bold uppercase tracking-wider mb-1.5 flex items-center gap-2">
              <span className="w-1 h-3 bg-amber-400 rounded-full" />
              Last Notes
            </div>
            <div className="text-[12px] font-medium text-slate-700 leading-relaxed italic">
              {lastNote}
            </div>
          </div>
        )}

        {/* Status Update Selector - Hidden as requested in original code */}
        {/* <StatusUpdateSelector 
          detail={detail}
          stages={stages}
          onUpdate={updateStatus}
          user={user}
          className="mt-1"
        /> */}
      </div>
    </DetailsWorkspaceModal>

    {/* Render Media Preview Modal */}
    {isMediaModalOpen && selectedMedia && (
      <MediaPreviewModal
        isOpen={isMediaModalOpen}
        onClose={() => setIsMediaModalOpen(false)}
        design={{
          customerName: detail.customerName || `${detail.firstName || ""} ${detail.lastName || ""}`.trim() || "Contact",
          firstName: detail.firstName,
          designMedia: [selectedMedia]
        }}
        initialMedia={selectedMedia}
      />
    )}
    </>
  );
};